import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import defaultLogo from "../../../../assets/img/auth/logo.jpg"; // ✅ fallback logo
import { API_BASE_URL } from "../../../../config";

const GenerateBillForm = ({ visit, onClose, onBillGenerated }) => {
  const [customer, setCustomer] = useState({});
  const [project, setProject] = useState({});
  const [admin, setAdmin] = useState({});
  const [admins, setAdmins] = useState([]); // ✅ all admins for dropdown
  const [logoUrl, setLogoUrl] = useState(defaultLogo);

  // Bill fields
  const [ratePerSqft, setRatePerSqft] = useState("");
  const [areaSqft, setAreaSqft] = useState("");
  const [basePrice, setBasePrice] = useState("");
  const [amenitiesCharge, setAmenitiesCharge] = useState("");
  const [parking, setParking] = useState("");
  const [extraCharges, setExtraCharges] = useState("");
  const [discount, setDiscount] = useState("");
  const [finalAgreedPrice, setFinalAgreedPrice] = useState("");
  const [commissionPercent, setCommissionPercent] = useState("");
  const [remarks, setRemarks] = useState("");
  const [generatedBy, setGeneratedBy] = useState(""); // ✅ dropdown value

  const token = localStorage.getItem("token");

  // 🔹 Fetch all admins (for dropdown)
  useEffect(() => {
    axios
      .get(
        `${API_BASE_URL}/api/admin/get-all-subadmin`,
        {
          headers: { token },
        }
      )
      .then((res) => {
        if (res.data.success) {
          setAdmins(res.data.result.subAdmins || []);
        }
      })
      .catch((err) => {
        // console.error("Failed to fetch admins:", err)
      }
      );
  }, [token]);

  // 🔹 Fetch company logo
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/admin/get-companylogo`, {
        headers: { token },
      })
      .then((res) => {
        const photoPath = res.data?.result?.profilePhoto;
        if (photoPath) {
          let finalPath = photoPath.startsWith("http")
            ? photoPath
            : `${API_BASE_URL}${photoPath.startsWith("/") ? photoPath : `/${photoPath}`
            }`;
          setLogoUrl(finalPath);
        } else {
          setLogoUrl(defaultLogo);
        }
      })
      .catch(() => setLogoUrl(defaultLogo));
  }, [token]);

  // 🔹 Fetch Admin details (Company Info)
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/admin/get-admin`, {
        headers: { token },
      })
      .then((res) => {
        if (res.data.success) setAdmin(res.data.result.admin || {}); setGeneratedBy(res.data.result.admin._id)
      })
      .catch((err) => console.error("Admin fetch error:", err));
  }, [token]);

  // 🔹 Fetch Customer & Project from visit
  useEffect(() => {
    if (visit?.customerId && typeof visit.customerId === "string") {
      axios
        .get(
          `${API_BASE_URL}/api/customer/get-customer/${visit.customerId}`,
          { headers: { token } }
        )
        .then((res) => {
          if (res.data.success) setCustomer(res.data.result.customer || {});
        })
        .catch((err) => console.error("Customer fetch error:", err));
    } else if (visit?.customerId) {
      setCustomer(visit.customerId);
    }

    if (visit?.projectId && typeof visit.projectId === "string") {
      axios
        .get(
          `${API_BASE_URL}/api/project/get-project/${visit.projectId}`,
          { headers: { token } }
        )
        .then((res) => {
          if (res.data.success) {
            setProject(res.data.result.project || {});
          }
        })
        .catch((err) => console.error("Project fetch error:", err));
    } else if (visit?.projectId) {
      setProject(visit.projectId);
    }
  }, [visit, token]);

  // 🔹 Auto calculation
  useEffect(() => {
    if (ratePerSqft && areaSqft) {
      const calculatedBase = Number(ratePerSqft) * Number(areaSqft);
      setBasePrice(calculatedBase.toFixed(2));
    }

    let subtotal =
      (Number(basePrice) || 0) +
      (Number(amenitiesCharge) || 0) +
      (Number(parking) || 0) +
      (Number(extraCharges) || 0) -
      (Number(discount) || 0);

    let total = subtotal;

    if (!isNaN(total)) {
      setFinalAgreedPrice(total.toFixed(2));
    }
  }, [
    ratePerSqft,
    areaSqft,
    basePrice,
    amenitiesCharge,
    parking,
    extraCharges,
    discount,
    commissionPercent,
  ]);

  // 🔹 Handle Generate
  const handleGenerate = async () => {
    if (!visit?._id || !customer?._id || !project?._id) {
      toast.error("Customer and Project not linked!");
      return;
    }
    if (!ratePerSqft || !areaSqft || !basePrice) {
      toast.error("Please fill required fields!");
      return;
    }
    if (!generatedBy) {
      toast.error("Please select Generated By!");
      return;
    }

    let parkingData = parking
      ? {
        type: "General",
        count: 1,
        rate: Number(parking),
        total: Number(parking),
      }
      : null;

    try {
      const payload = {
        visitId: visit._id,
        customerId: customer._id,
        projectId: project._id,
        generatedBy, // ✅ selected admin id
        ratePerSqft: Number(ratePerSqft),
        areaSqft: Number(areaSqft),
        basePrice: Number(basePrice),
        amenitiesCharge: Number(amenitiesCharge),
        parking: parkingData,
        extraCharges: Number(extraCharges),
        discount: Number(discount),
        commissionPercent: Number(commissionPercent),
        finalAgreedPrice: Number(finalAgreedPrice),
        remarks,
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/finalbillRecord/create-bill`,
        payload,
        { headers: { token } }
      );

      if (res.data.success) {
        toast.success("Bill generated successfully!");
        if (onBillGenerated) onBillGenerated(res.data.data);
        onClose();
      } else {
        toast.error(res.data.message || "Failed to generate bill.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error generating bill.");
    }
  };

  return (
    <div className="bg-black/30 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="max-h-[95vh] w-[950px] overflow-y-auto rounded-lg bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-4">
            <img
              src={logoUrl}
              alt="Company Logo"
              onError={() => setLogoUrl(defaultLogo)}
              className="h-20 w-20 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-blue-700">
                {admin.companyName || "Swaramayi Real Estates Marketing"}
              </h1>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded bg-red-700 px-2 py-2 text-white transition hover:bg-red-800"
          >
            ✖
          </button>
        </div>

        {/* Generated By Dropdown */}
        <div className="mb-6 grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium">Generated By</label>
            <select
              value={generatedBy}
              onChange={(e) => setGeneratedBy(e.target.value)}
              className="w-full rounded border p-2"
              disabled
            >
              <option value="">Select Admin</option>
              {admins.map((adm) => (
                <option key={adm._id} value={adm._id}>
                  {adm.adminName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Auto-filled Info */}
        <div className="mb-6 grid grid-cols-2 gap-6 rounded-lg border bg-gray-50 p-4">
          <div>
            <h3 className="mb-2 text-lg font-semibold">Customer Details</h3>
            <p>
              <strong>Name:</strong> {customer.customerName || "-"}
            </p>
            <p>
              <strong>Phone:</strong> {customer.customerNumber || "-"}
            </p>
            <p>
              <strong>Location:</strong> {customer.locationRequired || "-"}
            </p>
          </div>
          <div>
            <h3 className="mb-2 text-lg font-semibold">Project Details</h3>
            <p>
              <strong>Project:</strong> {project.projectName || "-"}
            </p>
            <p>
              <strong>Type:</strong> {project.projectType || "-"}
            </p>
            <p>
              <strong>Flat Type:</strong> {project.flatType || "-"}
            </p>
            <p>
              <strong>Flat Facing:</strong> {project.flatFacing || "-"}
            </p>
            <p>
              <strong>Amenities:</strong> {project.amenities || "-"}
            </p>
            <p>
              <strong>Address:</strong> {project.projectAddress || "-"}
            </p>
          </div>
        </div>

        {/* Table Section */}
        <table className="mb-6 w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">Rate/Sqft</th>
              <th className="border p-2">Area (Sqft)</th>
              <th className="border p-2">Base Price</th>
              <th className="border p-2">Amenities</th>
              <th className="border p-2">Parking</th>
              <th className="border p-2">Extra Charges</th>
              <th className="border p-2">Discount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border p-2">
                <input
                  type="number"
                  value={ratePerSqft}
                  onChange={(e) => setRatePerSqft(e.target.value)}
                  onBlur={() =>
                    setRatePerSqft(Number(ratePerSqft || 0).toFixed(2))
                  }
                  className="w-full rounded border p-1"
                />
              </td>
              <td className="border p-2">
                <input
                  type="number"
                  value={areaSqft}
                  onChange={(e) => setAreaSqft(e.target.value)}
                  // onBlur={() => setAreaSqft(Number(areaSqft || 0).toFixed(2))}
                  className="w-full rounded border p-1"
                />
              </td>
              <td className="border p-2">
                <input
                  type="number"
                  value={basePrice}
                  readOnly
                  className="w-full rounded border p-1"
                />
              </td>
              <td className="border p-2">
                <input
                  type="number"
                  value={amenitiesCharge}
                  onChange={(e) => setAmenitiesCharge(e.target.value)}
                  onBlur={() =>
                    setAmenitiesCharge(Number(amenitiesCharge || 0).toFixed(2))
                  }
                  className="w-full rounded border p-1"
                />
              </td>
              <td className="border p-2">
                <input
                  type="number"
                  value={parking}
                  onChange={(e) => setParking(e.target.value)}
                  onBlur={() => setParking(Number(parking || 0).toFixed(2))}
                  className="w-full rounded border p-1"
                />
              </td>
              <td className="border p-2">
                <input
                  type="number"
                  value={extraCharges}
                  onChange={(e) => setExtraCharges(e.target.value)}
                  onBlur={() =>
                    setExtraCharges(Number(extraCharges || 0).toFixed(2))
                  }
                  className="w-full rounded border p-1"
                />
              </td>
              <td className="border p-2">
                <input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  onBlur={() => setDiscount(Number(discount || 0).toFixed(2))}
                  className="w-full rounded border p-1"
                />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Summary */}
        {/* <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium">Valid Date</label>
            <input
              type="date"
              value={amountValidDate}
              onChange={(e) => setAmountValidDate(e.target.value)}
              className="border p-2 rounded w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Valid Time</label>
            <input
              type="time"
              value={amountValidTime}
              onChange={(e) => setAmountValidTime(e.target.value)}
              className="border p-2 rounded w-full"
            />
          </div>
        </div> */}

        <div className="mb-6 grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium">
              Final Agreed Price
            </label>
            <input
              type="number"
              value={finalAgreedPrice}
              readOnly
              className="w-full rounded border p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Commission %</label>
            <input
              type="number"
              value={commissionPercent}
              onChange={(e) => setCommissionPercent(e.target.value)}
              className="w-full rounded border p-2"
            />
          </div>
        </div>

        {/* Remarks */}
        <div className="mb-6">
          <label className="block text-sm font-medium">Remarks</label>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            className="w-full rounded border p-2"
            rows="3"
          ></textarea>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="rounded border px-5 py-2 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            className="rounded bg-blue-600 px-5 py-2 text-white hover:bg-blue-700"
          >
            Save & Generate Bill
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateBillForm;
